import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import {
  LoadIsOnboardingCompleted,
  LoadIsOnboardingCompletedIfNotLoaded,
} from '@layout/first-time-onboarding/store/actions/first-time-onboarding-completed.actions';
import { FirstTimeOnboardingCompletedService } from '@layout/first-time-onboarding/services/first-time-onboarding-completed.service';

interface FirstTimeOnboardingCompletedStateModel {
  isOnboardingCompleted?: boolean;
  error: boolean;
  loading: boolean;
  hasAttemptedLoad: boolean;
}

@State<FirstTimeOnboardingCompletedStateModel>({
  name: 'firstTimeOnboardingCompleted',
  defaults: {
    loading: false,
    error: false,
    hasAttemptedLoad: false,
  },
})
@Injectable()
export class FirstTimeOnboardingCompletedState {
  private readonly firstTimeOnboardingCompletedService = inject(FirstTimeOnboardingCompletedService);

  @Selector()
  static isOnboardingCompleted({ isOnboardingCompleted }: FirstTimeOnboardingCompletedStateModel): boolean | undefined {
    return isOnboardingCompleted;
  }

  @Selector()
  static loading({ loading }: FirstTimeOnboardingCompletedStateModel): boolean {
    return loading;
  }

  @Selector()
  static error({ error }: FirstTimeOnboardingCompletedStateModel): boolean {
    return error;
  }

  @Action(LoadIsOnboardingCompletedIfNotLoaded)
  loadIsOnboardingCompletedIfNotLoaded(context: StateContext<FirstTimeOnboardingCompletedStateModel>): Observable<void> {
    const { hasAttemptedLoad, loading } = context.getState();

    if (loading || hasAttemptedLoad) {
      return EMPTY;
    }

    return context.dispatch(LoadIsOnboardingCompleted);
  }

  @Action(LoadIsOnboardingCompleted)
  loadIsOnboardingCompleted(context: StateContext<FirstTimeOnboardingCompletedStateModel>): Observable<boolean> {
    context.patchState({ loading: true });

    return this.firstTimeOnboardingCompletedService.isOnboardingCompleted().pipe(
      tap(isOnboardingCompleted => context.patchState({ isOnboardingCompleted, error: false })),
      catchError(() => {
        context.patchState({ isOnboardingCompleted: undefined, error: true });
        return EMPTY;
      }),
      finalize(() => context.patchState({ loading: false, hasAttemptedLoad: true }))
    );
  }
}
